import type { FeatureDefinition } from "@growthbook/growthbook";
import { ssrFetcher } from "@website/fetcher/fetcher/ssrFetcher";

export type GBFeatures = Record<string, FeatureDefinition>;

export type GBFeaturesResponse = {
  features: GBFeatures;
};

const GB_FEATURES_TIMEOUT_MS = 5000;

const isGBFeaturesResponse = (
  response: unknown
): response is GBFeaturesResponse => {
  if (!response || typeof response !== "object" || !("features" in response)) {
    return false;
  }

  const { features } = response;

  const hasValidFeaturesShape =
    features !== null &&
    typeof features === "object" &&
    !Array.isArray(features);

  return hasValidFeaturesShape;
};

export async function configureExperimentServerSide(): Promise<GBFeatures> {
  const growthbookUrl = process.env.NEXT_PUBLIC_BASE_URL_GPI;
  const clientKey = process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY;

  //for apps without growthbook
  if (!clientKey || !growthbookUrl) {
    return {};
  }

  try {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutFallback = new Promise<GBFeaturesResponse>((resolve) => {
      timeoutId = setTimeout(
        () => resolve({ features: {} }),
        GB_FEATURES_TIMEOUT_MS
      );
    });

    const response = await Promise.race([
      ssrFetcher(`${growthbookUrl}/api/features/${clientKey}`, {
        next: { revalidate: 1800 }
      }),
      timeoutFallback
    ]).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });

    if (!isGBFeaturesResponse(response)) {
      return {};
    }

    return response.features;
  } catch {
    return {};
  }
}
